import { collection, doc, getDoc, getDocs, limit, orderBy, query, where, type Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { safeStrForConsole } from '../utils/safeErrorLogging';

export type KpoaCategory = 'injury' | 'illness';

export interface KpoaIncidentRow {
  id?: string;
  kpoaCategory: KpoaCategory;
  eventAt: Timestamp | { toDate: () => Date };
  eventDateRaw?: string;
  eventYearMonth?: string;
  fiscalQuarter: string;
  fiscalYear: number;
  departmentId: string;
  wardRaw?: string;
  ipdOpd: string;
  role: string;
  injuryType: string;
  severity: string;
  causePrimary: string;
  experienceYears?: number;
  ageYears?: number;
  notes?: string;
}

export interface KpoaIncidentMeta {
  totalRecordCount?: number;
  totalInjuryCount?: number;
  totalIllnessCount?: number;
  skippedUnmappedWard?: number;
  skippedInvalidDate?: number;
  skippedEmpty?: number;
  lastSyncAt?: { toDate: () => Date } | null;
  importSchemaVersion?: string;
}

export interface KpoaIncidentFetchOptions {
  departmentIds?: string[];
  limit?: number;
  /** Dashboards may hide unreadable departments, but exports must preserve auth failures. */
  preservePermissionErrors?: boolean;
}

export const sanitizeDepartmentIds = (ids?: readonly unknown[]) =>
  Array.from(
    new Set(
      (ids ?? [])
        .map((id) => (typeof id === 'string' ? id.trim() : safeStrForConsole(id).trim()))
        .filter(Boolean),
    ),
  );

const toStrField = (v: unknown): string =>
  typeof v === 'string' ? v : v && typeof (v as { id?: string }).id === 'string' ? (v as { id: string }).id : '';

export function kpoaIncidentRecordToRow(docId: string, d: Record<string, unknown>): KpoaIncidentRow {
  const catRaw = typeof d.kpoaCategory === 'string' ? d.kpoaCategory.trim().toLowerCase() : '';
  const kpoaCategory: KpoaCategory = catRaw === 'illness' ? 'illness' : 'injury';
  return {
    id: docId,
    kpoaCategory,
    eventAt: (d.eventAt as Timestamp) ?? (d.eventAt as { toDate: () => Date }),
    eventDateRaw: typeof d.eventDateRaw === 'string' ? d.eventDateRaw : undefined,
    eventYearMonth: typeof d.eventYearMonth === 'string' ? d.eventYearMonth : undefined,
    fiscalQuarter: typeof d.fiscalQuarter === 'string' ? d.fiscalQuarter : '',
    fiscalYear: typeof d.fiscalYear === 'number' && Number.isFinite(d.fiscalYear) ? d.fiscalYear : 0,
    departmentId: toStrField(d.departmentId),
    wardRaw: typeof d.wardRaw === 'string' ? d.wardRaw : undefined,
    ipdOpd: typeof d.ipdOpd === 'string' ? d.ipdOpd : '',
    role: typeof d.role === 'string' ? d.role : '',
    injuryType: typeof d.injuryType === 'string' ? d.injuryType : '',
    severity: typeof d.severity === 'string' ? d.severity : '',
    causePrimary: typeof d.causePrimary === 'string' ? d.causePrimary : '',
    experienceYears: typeof d.experienceYears === 'number' ? d.experienceYears : undefined,
    ageYears: typeof d.ageYears === 'number' ? d.ageYears : undefined,
    notes: typeof d.notes === 'string' ? d.notes : undefined,
  };
}

export async function fetchKpoaIncidentMeta(): Promise<KpoaIncidentMeta | null> {
  const metaSnap = await getDoc(doc(db, 'kpoaIncidentMeta', 'summary'));
  if (!metaSnap.exists()) return null;
  return metaSnap.data() as KpoaIncidentMeta;
}

export async function fetchKpoaIncidentData(options: KpoaIncidentFetchOptions = {}): Promise<KpoaIncidentRow[]> {
  const coll = collection(db, 'kpoaIncidents');
  const cap = Math.min(Math.max(options.limit ?? 1000, 1), 1000);

  const scoped = sanitizeDepartmentIds(options.departmentIds);
  const isAll = scoped.includes('*');
  const shouldScope = scoped.length > 0 && !isAll;

  const isPermissionDenied = (e: unknown): boolean => {
    const anyE = e as { code?: unknown; message?: unknown } | null | undefined;
    const c = typeof anyE?.code === 'string' ? anyE.code : '';
    const m = typeof anyE?.message === 'string' ? anyE.message : '';
    return c.includes('permission-denied') || m.includes('permission-denied') || m.includes('Missing or insufficient permissions');
  };

  // NOTE: Firestore Rules are not filters. If a user can only read some departments,
  // an unscoped query would be denied. So we scope queries to departmentId when provided.
  // For global-view posture (and for callers that don't provide explicit scope), use the simple
  // orderBy-only query to avoid needing composite indexes.
  if (!shouldScope) {
    try {
      const qy = query(coll, orderBy('eventAt', 'desc'), limit(cap));
      const snapshot = await getDocs(qy);
      return snapshot.docs.map((d) => kpoaIncidentRecordToRow(d.id, d.data() as Record<string, unknown>));
    } catch (e: unknown) {
      if (isPermissionDenied(e) && !options.preservePermissionErrors) return [];
      throw e;
    }
  }

  const toMillis = (v: unknown): number => {
    const anyV = v as { toMillis?: () => number; toDate?: () => Date } | null | undefined;
    if (anyV?.toMillis) return anyV.toMillis();
    const d = anyV?.toDate?.();
    return d ? d.getTime() : 0;
  };

  // Multi-dept scoping: Firestore doesn't support "IN" > 10 and we'd need composite indexes anyway.
  // Use per-department equality queries, then merge + sort client-side.
  const perDeptCap = Math.min(200, cap);
  const byId = new Map<string, KpoaIncidentRow>();
  await Promise.all(
    scoped.map(async (departmentId) => {
      try {
        const snap = await getDocs(
          // Avoid composite index requirement: equality filter + limit only.
          // (Sorting is applied after merging.)
          query(coll, where('departmentId', '==', departmentId), limit(perDeptCap)),
        );
        snap.docs.forEach((d) => {
          byId.set(d.id, kpoaIncidentRecordToRow(d.id, d.data() as Record<string, unknown>));
        });
      } catch (e: unknown) {
        // If the caller's scope list contains departments they can't read, skip those instead of failing the whole page.
        if (!isPermissionDenied(e) || options.preservePermissionErrors) throw e;
      }
    }),
  );

  return Array.from(byId.values())
    .sort((a, b) => toMillis(b.eventAt) - toMillis(a.eventAt))
    .slice(0, cap);
}

export interface KpoaIncidentAggregates {
  total: number;
  byCategory: Record<string, number>;
  bySeverity: Record<string, number>;
  byCausePrimary: Record<string, number>;
  byInjuryType: Record<string, number>;
  byRole: Record<string, number>;
  byQuarter: Record<string, number>;
  byIpdOpd: Record<string, number>;
  byDepartment: Record<string, number>;
}

/** Plan risk model: D×1 + E×3 + F×5 */
export function kpoaSeverityWeight(severity: string | undefined | null): number {
  const s = (severity ?? '').trim().toUpperCase();
  if (s === 'D') return 1;
  if (s === 'E') return 3;
  if (s === 'F') return 5;
  return 0;
}

export type WardRiskRow = {
  departmentId: string;
  count: number;
  riskScore: number;
  severeEf: number;
};

/** Rank wards by sum of severity-weighted scores (D/E/F). */
export function computeWardRiskRows(rows: KpoaIncidentRow[]): WardRiskRow[] {
  const byDept = new Map<string, { count: number; riskScore: number; severeEf: number }>();
  rows.forEach((r) => {
    const id = (r.departmentId ?? '').trim() || '—';
    const w = kpoaSeverityWeight(r.severity);
    const sev = (r.severity ?? '').trim().toUpperCase();
    const isEf = sev === 'E' || sev === 'F';
    const cur = byDept.get(id) ?? { count: 0, riskScore: 0, severeEf: 0 };
    cur.count += 1;
    cur.riskScore += w;
    if (isEf) cur.severeEf += 1;
    byDept.set(id, cur);
  });
  return Array.from(byDept.entries())
    .map(([departmentId, v]) => ({ departmentId, ...v }))
    .sort((a, b) => b.riskScore - a.riskScore);
}

export type ExperienceBand = '0–1' | '1–3' | '4–7' | '8+';

function experienceBandForYears(y: number | undefined): ExperienceBand {
  if (y == null || !Number.isFinite(y)) return '0–1';
  if (y < 1) return '0–1';
  if (y < 4) return '1–3';
  if (y < 8) return '4–7';
  return '8+';
}

export function computeExperienceBandCounts(rows: KpoaIncidentRow[]): Record<ExperienceBand, number> {
  const out: Record<ExperienceBand, number> = { '0–1': 0, '1–3': 0, '4–7': 0, '8+': 0 };
  rows.forEach((r) => {
    const b = experienceBandForYears(r.experienceYears);
    out[b] = (out[b] ?? 0) + 1;
  });
  return out;
}

export type ParetoRow = {
  name: string;
  count: number;
  pct: number;
  cumPct: number;
};

export function computeParetoFromCounts(counts: Record<string, number>, totalOverride?: number): ParetoRow[] {
  const total = Math.max(
    0,
    Number.isFinite(totalOverride ?? NaN)
      ? Math.max(0, totalOverride ?? 0)
      : Object.values(counts).reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0),
  );
  const rows = Object.entries(counts)
    .filter(([k, v]) => (k ?? '').trim() !== '' && k !== '—' && Number.isFinite(v) && v > 0)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  let cum = 0;
  return rows.map((r) => {
    const pct = total > 0 ? (100 * r.count) / total : 0;
    cum += pct;
    return { name: r.name, count: r.count, pct, cumPct: Math.min(100, cum) };
  });
}

export type SeverityMixTrendRow = {
  bucket: string;
  total: number;
  pctBySeverity: Record<string, number>;
  countBySeverity: Record<string, number>;
};

function stableBucketKey(v: string | undefined | null): string {
  const s = (v ?? '').trim();
  return s || '—';
}

export function computeSeverityMixTrend(
  rows: KpoaIncidentRow[],
  options: { bucket: 'fiscalQuarter' | 'eventYearMonth' },
): SeverityMixTrendRow[] {
  const byBucket = new Map<string, { total: number; bySeverity: Record<string, number> }>();
  rows.forEach((r) => {
    const bucketKey =
      options.bucket === 'fiscalQuarter' ? stableBucketKey(r.fiscalQuarter) : stableBucketKey(r.eventYearMonth);
    if (bucketKey === '—') return;
    const sev = (r.severity || '—').trim() || '—';
    const cur = byBucket.get(bucketKey) ?? { total: 0, bySeverity: {} };
    cur.total += 1;
    cur.bySeverity[sev] = (cur.bySeverity[sev] ?? 0) + 1;
    byBucket.set(bucketKey, cur);
  });

  const sorted = Array.from(byBucket.entries()).sort(([a], [b]) => a.localeCompare(b, 'th'));
  return sorted.map(([bucket, v]) => {
    const pctBySeverity: Record<string, number> = {};
    Object.entries(v.bySeverity).forEach(([sev, count]) => {
      pctBySeverity[sev] = v.total > 0 ? (100 * count) / v.total : 0;
    });
    return { bucket, total: v.total, pctBySeverity, countBySeverity: v.bySeverity };
  });
}

export type InjuryExperienceCell = {
  injuryType: string;
  band: ExperienceBand;
  count: number;
};

export type InjuryExperienceMatrix = {
  injuryTypes: string[];
  bands: ExperienceBand[];
  cells: InjuryExperienceCell[];
  totalsByBand: Record<ExperienceBand, number>;
  totalsByInjuryType: Record<string, number>;
  total: number;
};

export function computeInjuryTypeByExperience(rows: KpoaIncidentRow[], options?: { maxInjuryTypes?: number }): InjuryExperienceMatrix {
  const bands: ExperienceBand[] = ['0–1', '1–3', '4–7', '8+'];
  const totalsByBand: Record<ExperienceBand, number> = { '0–1': 0, '1–3': 0, '4–7': 0, '8+': 0 };
  const totalsByInjuryType: Record<string, number> = {};
  const cellMap = new Map<string, number>();

  rows.forEach((r) => {
    const it = (r.injuryType || '—').trim() || '—';
    if (it === '—') return;
    const band = experienceBandForYears(r.experienceYears);
    totalsByBand[band] = (totalsByBand[band] ?? 0) + 1;
    totalsByInjuryType[it] = (totalsByInjuryType[it] ?? 0) + 1;
    const key = `${it}||${band}`;
    cellMap.set(key, (cellMap.get(key) ?? 0) + 1);
  });

  const maxInjuryTypes = Math.min(Math.max(options?.maxInjuryTypes ?? 10, 1), 25);
  const injuryTypes = Object.entries(totalsByInjuryType)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxInjuryTypes)
    .map(([it]) => it);

  const cells: InjuryExperienceCell[] = [];
  injuryTypes.forEach((it) => {
    bands.forEach((band) => {
      const count = cellMap.get(`${it}||${band}`) ?? 0;
      cells.push({ injuryType: it, band, count });
    });
  });

  const total = Object.values(totalsByInjuryType).reduce((a, b) => a + b, 0);
  return { injuryTypes, bands, cells, totalsByBand, totalsByInjuryType, total };
}

/** Sorted quarter keys and counts for trend (uses fiscalQuarter string as bucket). */
export function computeQuarterTrendSeries(rows: KpoaIncidentRow[]): { quarterKey: string; count: number }[] {
  const m = new Map<string, number>();
  rows.forEach((r) => {
    const k = (r.fiscalQuarter ?? '').trim() || '—';
    m.set(k, (m.get(k) ?? 0) + 1);
  });
  return Array.from(m.entries())
    .map(([quarterKey, count]) => ({ quarterKey, count }))
    .sort((a, b) => a.quarterKey.localeCompare(b.quarterKey, 'th'));
}

/** When fiscal quarter is missing, group by `eventYearMonth` (YYYY-MM) for trend line. */
export function computeEventYearMonthTrendSeries(rows: KpoaIncidentRow[]): { quarterKey: string; count: number }[] {
  const m = new Map<string, number>();
  rows.forEach((r) => {
    const k = (r.eventYearMonth ?? '').trim() || '—';
    m.set(k, (m.get(k) ?? 0) + 1);
  });
  return Array.from(m.entries())
    .map(([quarterKey, count]) => ({ quarterKey, count }))
    .sort((a, b) => a.quarterKey.localeCompare(b.quarterKey));
}

export function countSevereEf(rows: KpoaIncidentRow[]): number {
  let n = 0;
  rows.forEach((r) => {
    const s = (r.severity ?? '').trim().toUpperCase();
    if (s === 'E' || s === 'F') n += 1;
  });
  return n;
}

function bucketIpdOpd(raw: string | undefined | null): string {
  const s = (raw ?? '').trim().toUpperCase();
  if (!s) return '—';
  if (s === 'IPD' || s.includes('IPD')) return 'IPD';
  if (s === 'OPD' || s.includes('OPD')) return 'OPD';
  return '—';
}

export function computeKpoaIncidentAggregates(rows: KpoaIncidentRow[]): KpoaIncidentAggregates {
  const byCategory: Record<string, number> = {};
  const bySeverity: Record<string, number> = {};
  const byCausePrimary: Record<string, number> = {};
  const byInjuryType: Record<string, number> = {};
  const byRole: Record<string, number> = {};
  const byQuarter: Record<string, number> = {};
  const byIpdOpd: Record<string, number> = {};
  const byDepartment: Record<string, number> = {};

  rows.forEach((r) => {
    const cat = r.kpoaCategory || '—';
    byCategory[cat] = (byCategory[cat] ?? 0) + 1;
    const sev = r.severity || '—';
    bySeverity[sev] = (bySeverity[sev] ?? 0) + 1;
    const cause = r.causePrimary || '—';
    byCausePrimary[cause] = (byCausePrimary[cause] ?? 0) + 1;
    const it = r.injuryType || '—';
    byInjuryType[it] = (byInjuryType[it] ?? 0) + 1;
    const role = r.role || '—';
    byRole[role] = (byRole[role] ?? 0) + 1;
    const q = r.fiscalQuarter || '—';
    byQuarter[q] = (byQuarter[q] ?? 0) + 1;
    const io = bucketIpdOpd(r.ipdOpd);
    byIpdOpd[io] = (byIpdOpd[io] ?? 0) + 1;
    const dept = r.departmentId || '—';
    byDepartment[dept] = (byDepartment[dept] ?? 0) + 1;
  });

  return {
    total: rows.length,
    byCategory,
    bySeverity,
    byCausePrimary,
    byInjuryType,
    byRole,
    byQuarter,
    byIpdOpd,
    byDepartment,
  };
}

